import React from "react";
import { motion } from "framer-motion";
import { ArrowDownToLine, ArrowUpFromLine, Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import StatusBadge from "./StatusBadge";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function TransactionTable({ transactions, isLoading, onRowClick, compact = false }) {
  let t = null;
  let language = "en";
  try {
    const langContext = useLanguage();
    t = langContext?.t;
    language = langContext?.language || "en";
  } catch (e) {
    // Not wrapped in LanguageProvider
  }

  const translate = (key) => {
    if (t) return t(key);
    const fallbacks = {
      transaction: "Transaction",
      provider: "Provider",
      type: "Type",
      amount: "Amount",
      status: "Status",
      date: "Date",
      noTransactionsFound: "No transactions found",
    };
    return fallbacks[key] || key;
  };
  const [copiedId, setCopiedId] = React.useState(null);

  const copyToClipboard = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="divide-y divide-slate-100">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="p-4 flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
        <p className="text-slate-500">{translate("noTransactionsFound")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {!compact && (
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <div className="col-span-3">{translate("transaction")}</div>
          <div className="col-span-2">{translate("provider")}</div>
          <div className="col-span-1">{translate("type")}</div>
          <div className="col-span-2 text-right">{translate("amount")}</div>
          <div className="col-span-2">{translate("status")}</div>
          <div className="col-span-2">{translate("date")}</div>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {transactions.map((tx, index) => (
          <motion.div
            key={tx.id || index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.02 * index }}
            onClick={() => onRowClick?.(tx)}
            className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer items-center"
          >
            <div className="col-span-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tx.type === "deposit" ? "bg-blue-100" : "bg-emerald-100"
                }`}>
                {tx.type === "deposit" ? (
                  <ArrowDownToLine className="h-4 w-4 text-blue-600" />
                ) : (
                  <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-mono text-sm font-medium text-slate-900 truncate">
                    {tx.transaction_id?.slice(0, 12)}...
                  </p>
                  <button
                    onClick={(e) => copyToClipboard(e, tx.transaction_id)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {copiedId === tx.transaction_id ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                {/* Provider code removed from here as it's now a column, or keep as secondary info? User asked for a column. */}
              </div>
            </div>
            <div className="col-span-2">
              <span className="text-sm text-slate-600 font-medium">
                {tx.provider_display || tx.provider_code || "-"}
              </span>
            </div>
            <div className="col-span-1">
              <span className={`text-sm font-medium capitalize ${tx.type === "deposit" ? "text-blue-600" : "text-emerald-600"
                }`}>
                {tx.type}
              </span>
            </div>
            <div className="col-span-2 text-right">
              <span className={`font-semibold ${tx.type === "deposit" ? "text-blue-600" : "text-emerald-600"
                }`}>
                {tx.type === "deposit" ? "+" : "-"}฿{tx.amount?.toLocaleString()}
              </span>
            </div>
            <div className="col-span-2">
              <StatusBadge status={tx.status} />
            </div>
            <div className="col-span-2 text-sm text-slate-500">
              {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : "N/A"}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}